import dayjs from '../../utils/dayjs';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  DataTable,
  Divider,
  InlineGrid,
  InlineStack,
  Link,
  List,
  Page,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import JourneyModal from './journey.modal';
import { DeleteIcon, DuplicateIcon, EditIcon, PlusIcon } from '@shopify/polaris-icons';

import { useGetJourney, useMutateGetJourney } from 'queries/journey.query';
import { useGetTripTimes, useMutateCloneTripTime, useMutateDeleteTripTime } from 'queries/trip_time.query';
import { PlusCircleIcon } from '@shopify/polaris-icons';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';

import __helpers from 'helpers/index';
import TripScheduleModal from './tripSchedule.modal';
import { useGetTripSchedules, useMutateDeleteTripSchedule } from 'queries/trip_schedule.query';
import TripTimeModal from './tripTime.modal';
import { useNotification } from 'context/NotificationContext';

export default function JourneyDetail() {
  let { journey_id } = useParams();
  let { hash } = useLocation();
  const { addNotification } = useNotification();

  const history = useNavigate();
  const [newModelactive, setNewModelactive] = useState(false);
  const toggleNewActive = useCallback(() => setNewModelactive((active) => !active), []);
  const { data: entity, isPending, isSuccess } = useGetJourney(journey_id);

  const {
    refetch: getTripTimes,
    data: tripTemplateData,
    isPending: tripTemplateLoading,
    isSuccess: tripTemplateDataLoadSuccessed,
  } = useGetTripTimes({
    limit: 100,
    trip_schedule_id: String(hash).replace('#', ''),
  });

  useEffect(() => {
    if (hash) {
      getTripTimes();
    }
  }, [hash]);

  const {
    data: tripScheduleData,
    isLoading: tripScheduleLoading,
    isSuccess: tripScheduleSuccess,
  } = useGetTripSchedules({
    journey_id: journey_id,
    limit: 20,
  });

  /** delete trip schedule */
  const { mutateAsync: deleteTripSchedule } = useMutateDeleteTripSchedule();
  const deleteTripScheduleCallback = useCallback(async (trip_schedule_id) => {
    const confirmed = await showDeleteConfirm(
      'Xóa lịch chạy?',
      'Bạn có chắc muốn xóa không? Hành động này không thể khôi phục. Những vé đã mua hoàn toàn không bị ảnh hưởng bởi việc xóa này.'
    );
    if (confirmed) {
      try {
        await deleteTripSchedule(trip_schedule_id);
      } catch (e) {}
    }
  }, []);

  /** Delete */
  const { mutateAsync: mutateDeleteTripTemplate } = useMutateDeleteTripTime();
  const { mutateAsync: mutateCloneTripTemplate } = useMutateCloneTripTime();
  const { showDeleteConfirm } = useDeleteConfirm();
  const deleteTripTimeCallback = useCallback(async (trip_template_id) => {
    const confirmed = await showDeleteConfirm(
      'Xóa giờ chạy?',
      'Bạn có chắc muốn xóa không? Hành động này không thể khôi phục. Những vé đã mua hoàn toàn không bị ảnh hưởng bởi việc xóa này.'
    );
    if (confirmed) {
      try {
        await mutateDeleteTripTemplate(trip_template_id);
      } catch (e) {}
    }
  }, []);

  const cloneTripTimeCallback = useCallback(async () => {
    //
    try {
      let trip_schedule_id = String(hash).replace('#', '');
      await mutateCloneTripTemplate(trip_schedule_id);
      addNotification('info', 'Đã sao chép dữ liệu thời gian...');
    } catch (e) {}
  }, [hash]);

  /** chức năng edit  */
  const currentTripTimeID = useRef(null);
  const editTripTimeCallback = useCallback((trip_time_id: any) => {
    currentTripTimeID.current = trip_time_id;
    setTripTimeShowModal(true);
  }, []);

  // Chuyển đổi giây sang giờ, phút, giây
  const formatTime = (_minutes: number) => {
    const seconds = _minutes * 60;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return [hours > 0 ? `${hours}h` : '', minutes > 0 ? `${minutes}p` : hours > 0 ? '0p' : ''].filter(Boolean).join(' ');
  };

  /** Modal  */
  const [tripScheduleShowModal, setTripScheduleShowModal] = useState(false);
  const currentTripSchedule = useRef(null);

  const [tripTimeShowModal, setTripTimeShowModal] = useState(false);
  const currentTripScheduleID = String(hash).replace('#', '');

  return (
    <>
      <Helmet>
        <title>{entity?.journey_name}</title>
      </Helmet>
      <Page
        compactTitle
        titleMetadata={
          <>
            <Badge tone="success">{`${__helpers.formatNumberWithCommas(entity?.journey_default_price ?? 0)} VND`}</Badge>
            {entity?.is_default === 1 && <Badge tone="magic">Mặc định</Badge>}
          </>
        }
        subtitle={`Tổng thời gian: ${formatTime(entity?.journey_total_time ?? 0)} - Khoảng cách ${entity?.journey_distance} km - Loại hình vận tải: ${entity?.seating?.seating_name ?? '--'}`}
        title={`${entity?.journey_name}`}
        backAction={{
          onAction: () => history('/journey'),
        }}
        primaryAction={{
          content: 'Thêm giờ chạy',
          onAction: () => {},
        }}
        secondaryActions={[
          {
            content: 'Sửa hành trình',
            onAction: () => toggleNewActive(),
          },
        ]}
      >
        {isPending && <SkeletonBodyText />}

        <Divider />

        <br />

        {isSuccess && (
          <BlockStack gap="400">
            <Text as="h3" variant="headingLg">
              Lịch chạy
            </Text>
            <InlineStack align="start" blockAlign="center" gap="400">
              {tripScheduleLoading && (
                <Box padding={'400'} borderWidth="0165" minWidth="168px">
                  <SkeletonBodyText lines={3} />
                </Box>
              )}
              {tripScheduleSuccess &&
                tripScheduleData?.body?.map((el) => {
                  const current_div = hash === String('#' + el.id);
                  return (
                    <Box
                      padding={'200'}
                      borderWidth={current_div ? '025' : '0165'}
                      minWidth="168px"
                      borderRadius="400"
                      borderColor={current_div ? 'border-focus' : 'border-info'}
                    >
                      <InlineGrid alignItems="start" gap={'200'}>
                        <div className="clickable" onClick={() => history(`#${el.id}`)}>
                          <Text as="p">Từ: {dayjs(new Date(el.valid_from)).format('HH:mm DD-MM-YYYY')}</Text>
                          <Text as="p">
                            {el.valid_to ? `Đến: ${dayjs(new Date(el.valid_to)).format('HH:mm DD-MM-YYYY')}` : 'Đến: Không thời hạn'}
                          </Text>
                        </div>
                        <InlineStack gap="400">
                          <Button
                            variant="plain"
                            size="slim"
                            icon={EditIcon}
                            onClick={() => {
                              currentTripSchedule.current = el.id;
                              setTripScheduleShowModal(true);
                            }}
                          >
                            Chỉnh
                          </Button>
                          <Button variant="plain" size="slim" tone="critical" icon={DeleteIcon} onClick={() => deleteTripScheduleCallback(el.id)}>
                            Xóa
                          </Button>
                        </InlineStack>
                      </InlineGrid>
                    </Box>
                  );
                })}

              <Button
                icon={PlusIcon}
                variant="plain"
                size="slim"
                onClick={() => {
                  setTripScheduleShowModal(true);
                  currentTripSchedule.current = null;
                }}
              >
                Thêm lịch chạy
              </Button>
            </InlineStack>

            <Divider />

            {hash && (
              <BlockStack gap="400">
                <Text as="h3" variant="headingLg">
                  Giờ chạy
                </Text>
                <Text as="p" tone="subdued">
                  Giờ chạy tại đây sẽ được hiển thị trên danh sách tìm và đặt vé. Các giờ tăng cường có thể được thêm sau vào mỗi hành trình.
                </Text>
                {tripTemplateLoading && <SkeletonBodyText />}
                {tripTemplateDataLoadSuccessed && (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric']}
                    headings={['Giờ', 'Giá', 'Giá gửi hàng', 'Loại hình', '-']}
                    rows={tripTemplateData?.body?.map((el) => {
                      return [
                        <div>
                          <Link onClick={() => editTripTimeCallback(el.id)}>
                            <strong>{el.time}</strong>
                          </Link>
                        </div>,
                        el.price ? __helpers.formatNumberWithCommas(el.price) : 0,
                        el.shipping_price ? __helpers.formatNumberWithCommas(el.shipping_price) : 0,
                        el.seating?.seating_name,
                        <Button icon={DeleteIcon} size="slim" variant="plain" onClick={() => deleteTripTimeCallback(el.id)}>
                          Xóa
                        </Button>,
                      ];
                    })}
                  />
                )}
                <InlineStack align="start" blockAlign="center" gap="200">
                  <Button
                    icon={PlusCircleIcon}
                    size="slim"
                    variant="plain"
                    onClick={() => {
                      currentTripTimeID.current = null;
                      setTripTimeShowModal(true);
                    }}
                  >
                    Thêm giờ chạy
                  </Button>
                  <Button icon={DuplicateIcon} size="slim" variant="plain" onClick={() => cloneTripTimeCallback()}>
                    Sao chép giờ gần nhất
                  </Button>
                </InlineStack>
              </BlockStack>
            )}
          </BlockStack>
        )}
        <br />
        <br />
      </Page>

      {
        <>
          <TripScheduleModal
            currentTripSchedule={currentTripSchedule.current}
            currentJourneyID={journey_id}
            show={tripScheduleShowModal}
            onClose={() => {
              setTripScheduleShowModal(false);
              currentTripSchedule.current = null;
            }}
          />

          <TripTimeModal
            onClose={() => {
              setTripTimeShowModal(false);
            }}
            show={tripTimeShowModal}
            currentTripScheduleID={currentTripScheduleID}
            currentTripTime={currentTripTimeID.current}
          />

          <JourneyModal
            show={newModelactive}
            currentJourneyID={BigInt(journey_id)}
            onClose={() => {
              setNewModelactive(false);
            }}
          />
        </>
      }
    </>
  );
}

